import type { OriginsQuery } from '@/shared/graphql/router/graphql'
import { useUserStore } from '@/shared/stores/user'
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

type Origin = OriginsQuery['origins'][number]

const CACHE_KEY = 'dacr:userOrigin'

function loadCachedOrigin(): Origin | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    if (!raw) return null
    return JSON.parse(raw) as Origin
  } catch {
    return null
  }
}

export const useOriginStore = defineStore('origin', () => {
  const origins = ref<OriginsQuery['origins']>([])
  const cachedUserOrigin = ref<Origin | null>(loadCachedOrigin())

  const setOrigins = (data: OriginsQuery['origins']) => {
    origins.value = data
  }

  const userOrigin = computed(() => {
    const userStore = useUserStore()
    const live = origins.value.find((o) => o.id === userStore.user?.origin)
    return live ?? cachedUserOrigin.value
  })

  watch(userOrigin, (origin) => {
    if (!origin) return
    cachedUserOrigin.value = origin
    localStorage.setItem(CACHE_KEY, JSON.stringify(origin))
  })

  const reset = () => {
    origins.value = []
    cachedUserOrigin.value = null
    localStorage.removeItem(CACHE_KEY)
  }

  return { origins, userOrigin, setOrigins, reset }
})
